import * as React from 'react'
import { ReactElement, useContext } from 'react'
import { Field, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import MoneyInput from '../../Input/MoneyInput'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import LegalAidHourInput from './LegalAidHourInput'
import { useFormikContext } from 'formik'
import useCurrency from '../../../hooks/useCurrency'
import LegalAidTravelDistanceInput from './LegalAidTravelDistanceInput'
import { LegalAidCertificatePanelProps } from './FormContainer'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { CommitEventType } from '../../Input/FormattableNumberInput'

const useStyles = makeStyles({
    compensationContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        ...shorthands.gap(tokens.spacingHorizontalM),
    },
})

export default function GrantPanel({ orientation, styles, invoicePending }: LegalAidCertificatePanelProps): ReactElement {
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { values, setFieldValue, errors } = useFormikContext<LegalAidCertificateDto>()
    const grantStyles = useStyles()
    const { currency } = useCurrency(values.currency)

    return (
        <div className={styles.formRow}>
            <Field
                label="Basisvergoeding"
                orientation={orientation}
                className={styles.field}
                validationState={errors.baseHours || errors.baseFee ? 'error' : undefined}
                validationMessage={`${errors.baseHours || ''} ${errors.baseFee || ''}`.trim()}
            >
                <div className={grantStyles.compensationContainer}>
                    <LegalAidHourInput
                        disabled={!!values.settlementInvoiceId || !!invoicePending}
                        styles={styles}
                        fieldName="baseHours"
                        pinnedFieldName={'baseFee'}
                        rate={settings.baseFeeHour}
                        currency={currency}
                    />
                    <MoneyInput
                        disabled={!!values.settlementInvoiceId || !!invoicePending}
                        value={values.baseFee}
                        currency={values.currency}
                        className={styles.input}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue('baseFee', value)
                            }
                        }}
                        tabIndex={-1}
                    />
                </div>
            </Field>
            <Field
                label="Reistijdvergoeding"
                orientation={orientation}
                className={styles.field}
                validationState={errors.travelHours || errors.travelFee ? 'error' : undefined}
                validationMessage={`${errors.travelHours || ''} ${errors.travelFee || ''}`.trim()}
            >
                <div className={grantStyles.compensationContainer}>
                    <LegalAidHourInput
                        disabled={!!values.settlementInvoiceId || !!invoicePending}
                        styles={styles}
                        fieldName="travelHours"
                        pinnedFieldName="travelFee"
                        rate={settings.travelFeeHour}
                        currency={currency}
                    />
                    <MoneyInput
                        disabled={!!values.settlementInvoiceId || !!invoicePending}
                        value={values.travelFee}
                        currency={values.currency}
                        className={styles.input}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue('travelFee', value)
                            }
                        }}
                        tabIndex={-1}
                    />
                </div>
            </Field>
            <Field
                label="Reiskostenvergoeding"
                orientation={orientation}
                className={styles.field}
                validationState={errors.travelDistance || errors.travelExpenses ? 'error' : undefined}
                validationMessage={`${errors.travelDistance || ''} ${errors.travelExpenses || ''}`.trim()}
            >
                <div className={grantStyles.compensationContainer}>
                    <LegalAidTravelDistanceInput
                        disabled={!!values.settlementInvoiceId || !!invoicePending}
                        styles={styles}
                        fieldName="travelDistance"
                        pinnedFieldName="travelExpenses"
                        rate={settings.travelKm}
                        currency={currency}
                    />
                    <MoneyInput
                        disabled={!!values.settlementInvoiceId || !!invoicePending}
                        value={values.travelExpenses}
                        currency={values.currency}
                        className={styles.input}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue('travelExpenses', value)
                            }
                        }}
                        tabIndex={-1}
                    />
                </div>
            </Field>
            <Field
                label="Eigen bijdrage"
                orientation={orientation}
                className={styles.field}
                validationState={errors.contribution ? 'error' : undefined}
                validationMessage={errors.contribution}
            >
                <MoneyInput
                    disabled={!!values.contributionInvoiceId || !!invoicePending}
                    value={values.contribution}
                    currency={values.currency}
                    onCommit={(eventType, value) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('contribution', value)
                        }
                    }}
                />
            </Field>
            <Field
                label="Vaste kostenvergoeding"
                orientation={orientation}
                className={styles.field}
                validationState={errors.fixedCompensation ? 'error' : undefined}
                validationMessage={errors.fixedCompensation}
            >
                <MoneyInput
                    disabled={!!values.settlementInvoiceId || !!invoicePending}
                    value={values.fixedCompensation}
                    currency={values.currency}
                    tabIndex={-1}
                    className={styles.input}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue('fixedCompensation', value)
                        }
                    }}
                />
            </Field>
        </div>
    )
}
